import React, { useEffect, useState } from "react";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  Mail,
  Phone,
  Smartphone,
  Star,
  UserPlus,
  Crown,
} from "lucide-react";
import {
  fetchDispensaryContacts,
  createDispensaryContact,
  updateDispensaryContact,
  deleteDispensaryContact,
} from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "owner",              label: "Owner" },
  { value: "general_manager",    label: "General Manager" },
  { value: "buyer",              label: "Buyer / Purchasing" },
  { value: "inventory_manager",  label: "Inventory Manager" },
  { value: "marketing",          label: "Marketing" },
  { value: "events",             label: "Events Coordinator" },
  { value: "budtender",          label: "Budtender" },
  { value: "other",              label: "Other" },
];

const ROLE_LABEL = Object.fromEntries(
  ROLE_OPTIONS.map((r) => [r.value, r.label])
);

// Short role label for the compact chip view. Long role names crowd
// the pill row, so we use a 1–2 word truncation.
const ROLE_SHORT = {
  owner: "Owner",
  general_manager: "GM",
  buyer: "Buyer",
  inventory_manager: "Inventory",
  marketing: "Marketing",
  events: "Events",
  budtender: "Budtender",
  other: "Other",
};

const ROLE_COLOR = {
  owner:             "bg-amber-500/15 text-amber-200 border-amber-500/30",
  general_manager:   "bg-violet-500/15 text-violet-200 border-violet-500/30",
  buyer:             "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  inventory_manager: "bg-sky-500/15 text-sky-200 border-sky-500/30",
  marketing:         "bg-pink-500/15 text-pink-200 border-pink-500/30",
  events:            "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/30",
  budtender:         "bg-cyan-500/15 text-cyan-200 border-cyan-500/30",
  other:             "bg-zinc-500/15 text-zinc-200 border-zinc-500/30",
};

const EMPTY_FORM = {
  name: "",
  role: "buyer",
  email: "",
  phone: "",
  mobile: "",
  notes: "",
  is_primary: false,
};

function initials(name) {
  return (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");
}

/**
 * Compact people view for the Dispensary Detail page.
 *
 * Default state: a single horizontal pill row showing avatar + first name
 * + short role chip for each person at this account. Click any pill to
 * expand a detail card below it (with email/phone click-to-contact +
 * edit / delete). Click "+" to add a new person.
 *
 * Goal: at-a-glance "who do I know here" without a tall, form-heavy
 * card cluttering the page above primary content (Tasks, Notes, Activity).
 */
export function DispensaryPeopleSection({ dispensaryId }) {
  const [contacts, setContacts] = useState(null);
  const [selectedId, setSelectedId] = useState(null);  // id | "new" | null
  const [mode, setMode] = useState("view");            // "view" | "edit" | "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!dispensaryId) return;
    try {
      const list = await fetchDispensaryContacts(dispensaryId);
      setContacts(list);
    } catch {
      setContacts([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispensaryId]);

  const togglePill = (c) => {
    if (selectedId === c.id) {
      setSelectedId(null);
      setMode("view");
    } else {
      setSelectedId(c.id);
      setMode("view");
    }
  };

  const startNew = () => {
    setForm(EMPTY_FORM);
    setSelectedId("new");
    setMode("new");
  };

  const startEdit = (c) => {
    setForm({
      name: c.name || "",
      role: c.role || "other",
      email: c.email || "",
      phone: c.phone || "",
      mobile: c.mobile || "",
      notes: c.notes || "",
      is_primary: !!c.is_primary,
    });
    setMode("edit");
  };

  const cancel = () => {
    setSelectedId(null);
    setMode("view");
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name,
        role: form.role,
        email: form.email.trim(),
        phone: form.phone.trim(),
        mobile: form.mobile.trim(),
        notes: form.notes.trim(),
        is_primary: form.is_primary,
      };
      if (mode === "new") {
        await createDispensaryContact(dispensaryId, payload);
        toast.success(`Added ${name}`);
      } else {
        await updateDispensaryContact(selectedId, payload);
        toast.success("Contact updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error("Could not save contact", { description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from this account?`)) return;
    try {
      await deleteDispensaryContact(c.id);
      toast.success(`Removed ${c.name}`);
      cancel();
      load();
    } catch {
      toast.error("Could not remove contact");
    }
  };

  const selected =
    selectedId && selectedId !== "new"
      ? (contacts || []).find((x) => x.id === selectedId)
      : null;

  return (
    <div className="panel p-4" data-testid="dispensary-people-section">
      {/* Compact header — single line with title + count + add btn */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-4 h-4 text-sky-300 shrink-0" />
          <h3 className="text-sm font-semibold text-zinc-100">People here</h3>
          {contacts && contacts.length > 0 && (
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              · {contacts.length} {contacts.length === 1 ? "contact" : "contacts"}
            </span>
          )}
        </div>
        {selectedId !== "new" && (
          <Button
            data-testid="add-person-btn"
            onClick={startNew}
            size="sm"
            variant="outline"
            className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add person
          </Button>
        )}
      </div>

      {/* Compact pill row — the "default" at-a-glance view */}
      {contacts === null ? (
        <div className="mt-3 text-xs text-zinc-500">Loading…</div>
      ) : contacts.length === 0 && selectedId !== "new" ? (
        <div
          data-testid="people-empty-state"
          className="mt-3 text-xs text-zinc-500 italic"
        >
          No buyers, managers, or owners logged yet. Add the people you talk
          to here so the rest of the team can reach them too.
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="people-pill-row">
          {(contacts || []).map((c) => (
            <PersonPill
              key={c.id}
              c={c}
              active={selectedId === c.id}
              onClick={() => togglePill(c)}
            />
          ))}
        </div>
      )}

      {/* Expanded card — appears below the pills when one is selected */}
      {selected && mode === "view" && (
        <PersonDetailCard
          c={selected}
          onEdit={() => startEdit(selected)}
          onDelete={() => remove(selected)}
          onClose={cancel}
        />
      )}
      {selectedId === "new" && mode === "new" && (
        <PersonForm
          form={form}
          setForm={setForm}
          onSave={save}
          onCancel={cancel}
          saving={saving}
          mode="new"
        />
      )}
      {selected && mode === "edit" && (
        <PersonForm
          form={form}
          setForm={setForm}
          onSave={save}
          onCancel={cancel}
          saving={saving}
          mode="edit"
        />
      )}
    </div>
  );
}

/** Compact name + role chip. Clickable to expand details below. */
function PersonPill({ c, active, onClick }) {
  const roleClass = ROLE_COLOR[c.role] || ROLE_COLOR.other;
  return (
    <button
      type="button"
      data-testid={`person-pill-${c.id}`}
      onClick={onClick}
      title={`${c.name} · ${ROLE_LABEL[c.role] || c.role}`}
      className={`group inline-flex items-center gap-2 px-2 py-1 rounded-full border transition-colors ${
        active
          ? "border-emerald-500/40 bg-emerald-500/10"
          : "border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800 hover:border-zinc-700"
      }`}
    >
      <span className="w-6 h-6 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] font-mono font-semibold text-zinc-200">
        {initials(c.name)}
      </span>
      <span className="text-xs text-zinc-100 font-medium pr-0.5 max-w-[120px] truncate">
        {c.name}
      </span>
      <span
        className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${roleClass}`}
      >
        {ROLE_SHORT[c.role] || c.role}
      </span>
      {c.is_primary && (
        <Crown className="w-3 h-3 text-amber-300" title="Primary contact" />
      )}
    </button>
  );
}

/** Read-only detail card shown when a pill is clicked. */
function PersonDetailCard({ c, onEdit, onDelete, onClose }) {
  return (
    <div
      data-testid={`person-detail-${c.id}`}
      className="mt-3 rounded-md border border-zinc-800 bg-zinc-950/60 px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-semibold text-zinc-50">
              {c.name}
            </span>
            <span
              className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                ROLE_COLOR[c.role] || ROLE_COLOR.other
              }`}
            >
              {ROLE_LABEL[c.role] || c.role}
            </span>
            {c.is_primary && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-400/10 text-amber-200 border-amber-400/30">
                <Crown className="w-2.5 h-2.5" /> Primary
              </span>
            )}
          </div>
          {(c.email || c.phone || c.mobile) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono pt-0.5">
              {c.email && (
                <a
                  data-testid={`person-email-${c.id}`}
                  href={`mailto:${c.email}`}
                  className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200"
                >
                  <Mail className="w-3 h-3" />
                  {c.email}
                </a>
              )}
              {c.phone && (
                <a
                  data-testid={`person-phone-${c.id}`}
                  href={`tel:${c.phone}`}
                  className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200"
                >
                  <Phone className="w-3 h-3" />
                  {c.phone}
                </a>
              )}
              {c.mobile && (
                <a
                  data-testid={`person-mobile-${c.id}`}
                  href={`tel:${c.mobile}`}
                  className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200"
                >
                  <Smartphone className="w-3 h-3" />
                  {c.mobile}
                </a>
              )}
            </div>
          )}
          {c.notes && (
            <div className="text-xs text-zinc-400 leading-relaxed mt-1 border-l-2 border-zinc-800 pl-2">
              {c.notes}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            data-testid={`person-edit-${c.id}`}
            onClick={onEdit}
            className="p-1.5 text-zinc-500 hover:text-zinc-200 rounded"
            aria-label="Edit person"
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            data-testid={`person-delete-${c.id}`}
            onClick={onDelete}
            className="p-1.5 text-zinc-500 hover:text-rose-300 rounded"
            aria-label="Remove person"
            title="Remove"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-zinc-500 hover:text-zinc-200 rounded ml-1"
            aria-label="Close"
            title="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Add/edit form (kept compact — fits inline below the pill row). */
function PersonForm({ form, setForm, onSave, onCancel, saving, mode }) {
  const set = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div
      data-testid={mode === "new" ? "person-form-new" : "person-form-edit"}
      className="mt-3 rounded-md border border-emerald-500/30 bg-zinc-950 px-4 py-3 space-y-2.5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1.5fr] gap-2">
        <Input
          autoFocus
          data-testid="person-name-input"
          value={form.name}
          onChange={set("name")}
          placeholder="Full name (e.g. Maya Chen)"
          className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
        />
        <Select
          value={form.role}
          onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}
        >
          <SelectTrigger
            data-testid="person-role-select"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            {ROLE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input
          data-testid="person-email-input"
          value={form.email}
          onChange={set("email")}
          placeholder="Email"
          type="email"
          className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
        />
        <Input
          data-testid="person-phone-input"
          value={form.phone}
          onChange={set("phone")}
          placeholder="Office phone"
          className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 font-mono"
        />
        <Input
          data-testid="person-mobile-input"
          value={form.mobile}
          onChange={set("mobile")}
          placeholder="Mobile"
          className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 font-mono"
        />
      </div>
      <Textarea
        data-testid="person-notes-input"
        value={form.notes}
        onChange={set("notes")}
        rows={2}
        placeholder="Optional notes — preferences, schedule, etc."
        className="bg-zinc-900 border-zinc-800 text-zinc-100 text-xs resize-none"
      />
      <div className="flex items-center justify-between">
        <label className="inline-flex items-center gap-2 text-xs text-zinc-300 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="person-primary-toggle"
            checked={!!form.is_primary}
            onChange={(e) =>
              setForm((p) => ({ ...p, is_primary: e.target.checked }))
            }
            className="accent-amber-400"
          />
          <Star className="w-3.5 h-3.5 text-amber-300" />
          Primary contact at this account
        </label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onCancel}
            className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 h-8"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="person-save-btn"
            size="sm"
            onClick={onSave}
            disabled={saving || !form.name.trim()}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-8"
          >
            {saving ? "Saving…" : mode === "new" ? "Add person" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DispensaryPeopleSection;
